import React from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef } from 'react'

const CaseStudy = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.2
      }
    }
  }

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  }

  return (
    <section ref={ref} className="section-padding bg-gray-900/30">
      <div className="container-custom">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="text-center max-w-4xl mx-auto"
        >
          {/* Section Header */}
          <motion.div variants={itemVariants}>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
              Application <span className="gradient-text">Showcase</span>
            </h2>
            <p className="text-xl text-gray-300 mb-12 leading-relaxed">
              Explore the custom applications we've built. Each project includes detailed blog posts 
              and links to live applications.
            </p>
          </motion.div>

          {/* Coming Soon Card */}
          <motion.div
            variants={itemVariants}
            className="relative bg-gradient-to-br from-background/80 to-background/60 backdrop-blur-sm border border-gray-800 rounded-3xl p-12 md:p-16"
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent rounded-3xl" />
            
            {/* Content */}
            <div className="relative z-10">
              <div className="w-24 h-24 bg-primary/20 rounded-2xl flex items-center justify-center mx-auto mb-8">
                <img 
                  src="/logo.png" 
                  alt="Modumatic Logo" 
                  className="w-16 h-16 object-contain"
                />
              </div>
              
              <h3 className="text-2xl md:text-3xl font-heading font-semibold text-white mb-4">
                Our Applications
              </h3>
              
              <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
                Discover the custom applications and DevOps solutions we've built. 
                Each project includes a detailed blog post and a link to the live application.
              </p>

              {/* Placeholder Stats */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-8">
                {[
                  { number: "100%", label: "Custom Built" },
                  { number: "99.9%", label: "Uptime" },
                  { number: "Modern", label: "Tech Stack" }
                ].map((stat, index) => (
                  <div key={stat.label} className="text-center">
                    <div className="text-2xl md:text-3xl font-bold gradient-text mb-1">
                      {stat.number}
                    </div>
                    <div className="text-sm text-gray-400">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

            </div>

            {/* Floating Elements */}
            <motion.div
              className="absolute top-8 right-8 w-4 h-4 bg-primary/30 rounded-full"
              animate={{
                y: [0, -10, 0],
                opacity: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <motion.div
              className="absolute bottom-8 left-8 w-6 h-6 bg-primary/20 rounded-full"
              animate={{
                y: [0, 15, 0],
                opacity: [0.3, 0.7, 0.3],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </motion.div>

          {/* Bottom Note */}
          <motion.p
            variants={itemVariants}
            className="text-gray-400 mt-8 text-sm"
          >
            Each application includes a detailed blog post explaining the problem, solution, 
            technologies used, and a link to the live application.
          </motion.p>
        </motion.div>
      </div>
    </section>
  )
}

export default CaseStudy
